/**
 * +personas: the simulated player behind a bot (content/personas). Wraps the ordinary BotBrain
 * (brain.ts) with a small behaviour state machine and a chat voice:
 *
 *   normal ──(pull ends, afk roll)──▶ afk ──(timer / combat in companion mode)──▶ normal
 *   normal ──(pull ends, greed roll, node nearby)──▶ wandering ──(gathered / combat)──▶ normal
 *   normal ──(tilt over the quit line, unhinged only)──▶ quit (Command 'leaveParty', for good)
 *
 * Tilt is a number fed by the party's mistakes (deaths, repeat deaths to the same ability, wipes,
 * missed kicks, a slipping timer), weighted by temper and decaying with patience. Above 0.5 the
 * persona is "tilted" (salty lines); past its quit line an unhinged persona rolls a rage quit.
 *
 * Modes (PersonaMode):
 *   - 'companion' (a human is in the party): chat, brief AFKs and wandering, ONLY out of combat,
 *     never while someone else is away, always back by themselves (and at once when a pull starts);
 *     never quits. The bot tank waits for away members (movement.ts AWAY_WAIT_MS).
 *   - 'unhinged' (all-bot runs): AFK can run long (or forever: kicked after AFK_KICK_MS) and
 *     occasionally starts mid-pull; greedy wanderers ignore nearby packs (and pull them); tilted
 *     personas rage-quit; after someone leaves the others may abandon the key.
 *
 * Everything the party sees goes through sim Commands ('say', 'presence', 'leaveParty'), so chat,
 * AFK tags and quits replay identically on every client. All randomness comes from the persona's
 * own Rng (persona.seed × bot seed): no Math.random, no clock.
 */
import type { ChatSituation, Persona, PersonaMode } from '@keystone/content';
import { ordinal } from '@keystone/content';
import type { BotBrain, BotView, Command, Ms, Rng, SimEvent, UnitId, Vec2 } from '@keystone/sim';
import { createSeededRng } from '@keystone/sim';
import { dist } from '../geometry';
import { ChatVoice, type LineVars } from './chat';

export type PersonaState = 'normal' | 'afk' | 'wandering' | 'tilted' | 'quit';

/** Structured incident for narration / grudges (idle system, harness). */
export interface PersonaIncident {
  at: Ms;
  kind: 'ragequit' | 'abandon' | 'afk' | 'afk_kick' | 'wander' | 'tilt';
  unitId: UnitId;
  personaId: string;
  name: string;
  /** Whom they blame (rage quits, tilt). */
  blameId?: UnitId;
  blameName?: string;
  /** "Pip died to Venom Spit (3rd time)". */
  cause?: string;
  /** AFK / wander length (filled in when it ends). */
  durationMs?: Ms;
  /** Started during a pull. */
  inCombat?: boolean;
}

export interface PersonaBrain extends BotBrain {
  readonly persona: Persona;
  readonly mode: PersonaMode;
  readonly state: PersonaState;
  readonly tilt: number;
  readonly incidents: readonly PersonaIncident[];
  /** Chat lines this bot sent (tests, harness). */
  readonly linesSaid: number;
}

// ---- tuning ------------------------------------------------------------------------------------

/** Tilt added per mistake (× temper). */
export const TILT = {
  death: 0.2,
  /** Extra per earlier death of the same victim this run. */
  repeatDeath: 0.06,
  ownDeath: 0.06,
  wipe: 0.3,
  kickMissed: 0.04,
  lowTimer: 0.15,
  /** Someone else goes AFK / wanders off (waiting is the worst), + per earlier time. */
  away: 0.14,
  repeatAway: 0.08,
} as const;
/** Tilt decay time constant: TILT_TAU_MS[0] + patience × TILT_TAU_MS[1]. */
const TILT_TAU_MS: readonly [Ms, Ms] = [60_000, 240_000];
export const TILTED_AT = 0.5;
const TILT2_AT = 0.9;
/** Quit line: QUIT_BASE + patience × QUIT_PER_PATIENCE. */
export const QUIT_BASE = 0.75;
export const QUIT_PER_PATIENCE = 2;
/** Chance to actually leave per tilt-raising event past the quit line (+ how far past it). */
const QUIT_ROLL = 0.3;

/** Out-of-combat AFK chance per pull gap: traits.afk × this (companion uses a smaller scale). */
const AFK_PER_GAP = { companion: 0.06, unhinged: 0.12 } as const;
/** Mid-pull AFK chance per pull start (unhinged only): traits.afk × this. */
const AFK_MID_PULL = 0.03;
const AFK_MS = { companion: [5000, 25_000], unhinged: [8000, 45_000], long: [60_000, 150_000], midPull: [4000, 15_000] } as const;
/** Unhinged: chance an AFK runs long, and (× traits.afk) that they never come back. */
const AFK_LONG_CHANCE = 0.15;
const AFK_FOREVER_CHANCE = 0.06;
/** A never-back AFK leaves the group (is "kicked") after this long. */
export const AFK_KICK_MS: Ms = 150_000;
/** Wander chance per pull gap: traits.greed × this. */
const WANDER_PER_GAP = { companion: 0.12, unhinged: 0.2 } as const;
const WANDER_RANGE = { companion: 30, unhinged: 55 } as const;
/** Companion wanderers keep this far from any idle enemy (and from the walk to the node). */
const WANDER_SAFE_YD = 22;
const WANDER_GIVE_UP_MS: Ms = 20_000;
const GATHER_MS: readonly [Ms, Ms] = [2000, 4000];
/** After someone leaves (unhinged): chance each remaining persona abandons too. */
const ABANDON_CHANCE = { core: 0.6, dps: 0.3, few: 0.9 } as const;
/** Ambient chatter: mean gap between unprompted lines, chatty 0 → 1. */
const AMBIENT_MEAN_MS: readonly [Ms, Ms] = [32_000, 6000];
/** Ambient lines during a pull are rarer (× this). */
const AMBIENT_IN_COMBAT = 0.35;
/** Engaged enemies at which a pull counts as big. */
const BIG_PULL = 6;

export interface PersonaBotOptions {
  inner: BotBrain;
  persona: Persona;
  mode: PersonaMode;
  /** The bot's own seed (createBot's); mixed with persona.seed for the persona's Rng. */
  seed: number;
}

interface Pending {
  at: Ms;
  cmd: 'afk' | 'wander' | 'quit' | 'abandon' | 'afkKick';
  blameId?: UnitId;
  cause?: string;
}

export function createPersonaBot(opts: PersonaBotOptions): PersonaBrain {
  const { inner, persona, mode } = opts;
  const T = persona.traits;
  const unitId = inner.unitId;
  const rng: Rng = createSeededRng((Math.imul(opts.seed >>> 0, 0x9e3779b1) ^ (persona.seed >>> 0) ^ 0x5bd1e995) >>> 0);
  const voice = new ChatVoice(persona, rng);
  const unhinged = mode === 'unhinged';
  const incidents: PersonaIncident[] = [];

  let state: Exclude<PersonaState, 'tilted'> = 'normal';
  let tilt = 0;
  let tiltSaid = 0; // 0 none, 1 tilt_1, 2 tilt_2
  let lastTiltAt: Ms = 0;
  const quitAt = QUIT_BASE + QUIT_PER_PATIENCE * T.patience;
  let blame: { id: UnitId; cause: string; weight: number } | null = null;

  // Observer state (diffs of the world between calls).
  let started = false;
  let ended = false;
  let prevAlive = new Map<UnitId, boolean>();
  let prevWipes = 0;
  let prevPulls = 0;
  let prevCombat = false;
  let prevEnc: string | null = null;
  let prevEncName: string | null = null;
  const awaysOf = new Map<UnitId, number>();
  let prevBosses = 0;
  let combatSince: Ms | null = null;
  let bigChecked = true;
  let oocSince: Ms = 0;
  let lowTimerSaid = false;
  const deathsOf = new Map<UnitId, number>();
  const deathsTo = new Map<string, number>();
  const departedSeen = new Set<UnitId>();
  let nextAmbientAt: Ms = -1;
  let pending: Pending[] = [];

  // AFK / wander state.
  let awayStartedAt: Ms = 0;
  let afkUntil: Ms = 0;
  let afkForever = false;
  let afkLongSaid = false;
  let afkIncident: PersonaIncident | null = null;
  let wander: { id: string; pos: Vec2; gatherUntil: Ms | null; lastMoveAt: Ms } | null = null;
  let backReactAt: Ms | null = null;
  let tilted = false;

  const name = (view: BotView, id: UnitId | undefined): string => (id ? (view.world.units[id]?.name ?? id) : '');
  const humanName = (view: BotView): string | undefined => {
    for (const id of view.world.partyIds) {
      const u = view.world.units[id];
      if (u && u.controller === 'human' && !u.departed) return u.name;
    }
    return undefined;
  };
  const say = (view: BotView, sit: ChatSituation, vars: LineVars = {}, o: { force?: boolean; boost?: number; delayMs?: Ms } = {}): boolean =>
    voice.offer(sit, view.now, { self: name(view, unitId), human: humanName(view), ...vars }, o);

  const record = (view: BotView, inc: Omit<PersonaIncident, 'unitId' | 'personaId' | 'name' | 'at'>): PersonaIncident => {
    const full: PersonaIncident = { at: view.now, unitId, personaId: persona.id, name: name(view, unitId), ...inc };
    incidents.push(full);
    return full;
  };

  // ---- tilt -------------------------------------------------------------------------------------

  const decayTilt = (now: Ms): void => {
    const dt = now - lastTiltAt;
    lastTiltAt = now;
    if (tilt <= 0 || dt <= 0) return;
    tilt *= Math.exp(-dt / (TILT_TAU_MS[0] + TILT_TAU_MS[1] * T.patience));
    if (tilt < 0.3) tiltSaid = 0;
  };

  const addTilt = (view: BotView, amount: number, who?: { id: UnitId; cause: string }): void => {
    if (amount <= 0 || state === 'quit') return;
    const add = amount * (0.25 + T.temper);
    tilt += add;
    if (who && (!blame || add >= blame.weight * 0.8)) blame = { id: who.id, cause: who.cause, weight: add };
    const salty = T.toxicity > 0.35 || persona.archetype === 'tryhard';
    if (tilt >= TILT2_AT && tiltSaid < 2) {
      tiltSaid = 2;
      if (salty) say(view, 'tilt_2', { victim: name(view, blame?.id) }, { delayMs: 1500 });
    } else if (tilt >= TILTED_AT && tiltSaid < 1) {
      tiltSaid = 1;
      if (salty) say(view, 'tilt_1', { victim: name(view, blame?.id) }, { delayMs: 1500 });
    }
    if (tilt >= TILTED_AT && !tilted) {
      tilted = true;
      record(view, { kind: 'tilt', ...(blame ? { blameId: blame.id, blameName: name(view, blame.id), cause: blame.cause } : {}) });
    } else if (tilt < TILTED_AT * 0.6) tilted = false;
    if (unhinged && tilt >= quitAt && !pending.some((p) => p.cmd === 'quit') && rng.chance(Math.min(0.9, QUIT_ROLL + (tilt - quitAt)))) {
      pending.push({ at: view.now + rng.range(2000, 6000), cmd: 'quit', ...(blame ? { blameId: blame.id, cause: blame.cause } : {}) });
    }
  };

  // ---- away states ------------------------------------------------------------------------------

  const startAfk = (view: BotView, out: Command[], inCombat: boolean): void => {
    const range = inCombat ? AFK_MS.midPull : unhinged ? AFK_MS.unhinged : AFK_MS.companion;
    let len = rng.range(range[0], range[1]);
    afkForever = false;
    if (unhinged && !inCombat) {
      if (rng.chance(AFK_FOREVER_CHANCE * T.afk)) afkForever = true;
      else if (rng.chance(AFK_LONG_CHANCE)) len = rng.range(AFK_MS.long[0], AFK_MS.long[1]);
    }
    state = 'afk';
    awayStartedAt = view.now;
    afkUntil = afkForever ? Infinity : view.now + len;
    afkLongSaid = false;
    backReactAt = null;
    out.push({ type: 'stop', actorId: unitId }, { type: 'presence', actorId: unitId, status: 'afk' });
    say(view, 'afk_start', {}, { force: rng.chance(0.85) });
    afkIncident = record(view, { kind: 'afk', ...(inCombat ? { inCombat: true } : {}) });
    if (afkForever) pending.push({ at: view.now + AFK_KICK_MS, cmd: 'afkKick' });
  };

  const endAway = (view: BotView, out: Command[]): void => {
    const was = state;
    state = 'normal';
    backReactAt = null;
    out.push({ type: 'presence', actorId: unitId, status: null });
    if (was === 'afk') {
      if (afkIncident) afkIncident.durationMs = view.now - awayStartedAt;
      afkIncident = null;
      say(view, 'afk_back', {}, { force: rng.chance(0.6) });
    } else if (was === 'wandering') {
      const inc = [...incidents].reverse().find((i) => i.kind === 'wander');
      if (inc && inc.durationMs === undefined) inc.durationMs = view.now - awayStartedAt;
      wander = null;
      say(view, 'wander_back');
    }
  };

  /** A node / cache worth wandering to (companion: well clear of idle enemies). */
  const wanderTarget = (view: BotView, self: { pos: Vec2 }): { id: string; pos: Vec2 } | null => {
    const objs = view.world.worldObjects ?? [];
    const range = unhinged ? WANDER_RANGE.unhinged : WANDER_RANGE.companion;
    const idle: Vec2[] = [];
    if (!unhinged) {
      for (const id of view.world.unitOrder) {
        const u = view.world.units[id];
        if (u && u.alive && u.faction === 'enemy' && !u.inCombat) idle.push(u.pos);
      }
    }
    let best: { id: string; pos: Vec2; d: number } | null = null;
    for (const o of objs) {
      if (o.broken || o.hidden || !o.discovered || (o.class !== 'node' && o.class !== 'cache')) continue;
      if (o.z !== undefined) continue; // +glide: bots never go after sky chests
      const d = dist(self.pos, o.pos);
      if (d > range || d < 4) continue;
      if (!unhinged && idle.some((e) => dist(e, o.pos) < WANDER_SAFE_YD || segDist(e, self.pos, o.pos) < WANDER_SAFE_YD)) continue;
      if (!best || d < best.d) best = { id: o.id, pos: { x: o.pos.x, y: o.pos.y }, d };
    }
    return best ? { id: best.id, pos: best.pos } : null;
  };

  const startWander = (view: BotView, out: Command[], target: { id: string; pos: Vec2 }): void => {
    state = 'wandering';
    awayStartedAt = view.now;
    wander = { ...target, gatherUntil: null, lastMoveAt: view.now };
    out.push({ type: 'presence', actorId: unitId, status: 'wandering' }, { type: 'move', actorId: unitId, dest: target.pos });
    say(view, 'wander_start', {}, { force: rng.chance(0.7) });
    record(view, { kind: 'wander' });
  };

  const leave = (view: BotView, out: Command[], reason: 'ragequit' | 'abandon' | 'afk', p?: Pending): void => {
    if (state === 'quit') return;
    if (state === 'afk' || state === 'wandering') {
      if (afkIncident) afkIncident.durationMs = view.now - awayStartedAt;
    }
    state = 'quit';
    voice.clear();
    const blameName = name(view, p?.blameId);
    if (reason === 'ragequit') {
      record(view, { kind: 'ragequit', ...(p?.blameId ? { blameId: p.blameId, blameName } : {}), ...(p?.cause ? { cause: p.cause } : {}) });
      const line = voice.offer('rage_quit', view.now, { victim: blameName, self: name(view, unitId) }, { force: true });
      if (line) for (const l of voice.due(Infinity)) out.push(sayCmd(l));
    } else if (reason === 'abandon') {
      record(view, { kind: 'abandon', ...(p?.cause ? { cause: p.cause } : {}) });
      if (voice.offer('abandon', view.now, {}, { force: rng.chance(0.7) })) for (const l of voice.due(Infinity)) out.push(sayCmd(l));
    } else record(view, { kind: 'afk_kick', cause: 'AFK too long' });
    out.push({ type: 'leaveParty', actorId: unitId, reason, ...(p?.blameId ? { blameId: p.blameId } : {}), ...(p?.cause ? { cause: p.cause } : reason === 'afk' ? { cause: 'AFK too long' } : {}) });
  };

  const sayCmd = (l: { text: string; lineId: string; emote: boolean }): Command => ({ type: 'say', actorId: unitId, text: l.text, lineId: l.lineId, ...(l.emote ? { emote: true } : {}) });

  // ---- observing the party --------------------------------------------------------------------

  const observe = (view: BotView, out: Command[]): void => {
    const w = view.world;
    const now = view.now;
    const m = w.mplus;
    const self = w.units[unitId];
    if (!self) return;
    const events = view.events ?? [];

    if (!started && (m.phase === 'running' || m.phase === 'pre') && now > 0) {
      started = true;
      for (const id of w.partyIds) prevAlive.set(id, !!w.units[id]?.alive);
      prevWipes = m.wipes;
      prevPulls = m.pulls;
      prevBosses = m.bossesKilled.length;
      oocSince = now;
      if (!humanName(view) || !rng.chance(0.5)) say(view, 'run_start', {}, { delayMs: rng.range(500, 4000) });
      else say(view, 'greet_human', {}, { delayMs: rng.range(800, 5000) });
      nextAmbientAt = now + ambientGap();
      return;
    }
    if (!started) return;

    // Deaths (alive → dead; a member who left is not a death).
    const alive = new Map<UnitId, boolean>();
    for (const id of w.partyIds) {
      const u = w.units[id];
      if (!u) continue;
      alive.set(id, u.alive);
      if (u.departed) {
        if (!departedSeen.has(id)) {
          departedSeen.add(id);
          if (id !== unitId) onLeft(view, id, events);
        }
        continue;
      }
      if (prevAlive.get(id) && !u.alive) onDeath(view, id, events);
    }
    prevAlive = alive;

    // Pulls / combat edges.
    const inCombat = m.inCombat;
    if (inCombat && !prevCombat) onPullStart(view, out);
    if (!inCombat && prevCombat) onPullEnd(view);
    prevCombat = inCombat;
    if (!bigChecked && combatSince !== null && now - combatSince >= 1500) {
      bigChecked = true;
      const engaged = w.unitOrder.reduce((n, id) => {
        const u = w.units[id];
        return u && u.alive && u.faction === 'enemy' && u.inCombat ? n + 1 : n;
      }, 0);
      if (engaged >= BIG_PULL && !w.encounter) say(view, 'big_pull', {}, { boost: isCaller(view) ? 1.3 : 0.6 });
    }
    if (m.wipes > prevWipes) {
      prevWipes = m.wipes;
      const worst = worstDier(view);
      const where = prevEncName ? ` on ${prevEncName}` : '';
      const again = m.wipes > 1 ? ` (wipe ${m.wipes})` : '';
      addTilt(view, TILT.wipe, worst ? { id: worst.id, cause: `the group wiped${where}${again}, ${name(view, worst.id)} died ${worst.n}× this run` } : undefined);
      say(view, 'wipe', {}, { delayMs: rng.range(500, 3000) });
    }
    const enc = w.encounter && w.encounter.result === null ? w.encounter.encounterId : null;
    if (enc && enc !== prevEnc) {
      prevEncName = view.content.encounter(enc)?.name ?? null;
      say(view, 'boss_pull', { boss: prevEncName ?? undefined }, { boost: isCaller(view) ? 1.4 : 0.6 });
    } else if (!enc && !inCombat) prevEncName = null;
    prevEnc = enc;
    if (m.bossesKilled.length > prevBosses) {
      prevBosses = m.bossesKilled.length;
      tilt *= 0.7;
      say(view, 'boss_kill', {}, { delayMs: rng.range(300, 2500) });
    }

    // Events of the last step: kicks, other members' chat, caches.
    for (const e of events) onEvent(view, e);

    // Timer.
    if (!lowTimerSaid && m.phase === 'running' && m.timerMs > 0 && m.elapsedMs > m.timerMs * 0.8) {
      const done = m.forcesRequired > 0 ? m.forces / m.forcesRequired : 1;
      if (done < 0.95) {
        lowTimerSaid = true;
        addTilt(view, TILT.lowTimer);
        say(view, 'low_timer');
      }
    }
    if (!ended && (m.phase === 'completed' || m.phase === 'depleted')) {
      ended = true;
      if (!m.abandoned) say(view, m.phase === 'completed' ? 'timed' : 'depleted', {}, { delayMs: rng.range(500, 4000), boost: 1.2 });
    }

    // Ambient chatter (mostly between pulls).
    if (state === 'normal' && nextAmbientAt >= 0 && now >= nextAmbientAt) {
      nextAmbientAt = now + ambientGap();
      if (inCombat) say(view, 'ambient', {}, { boost: AMBIENT_IN_COMBAT });
      else if (now - oocSince > 3000) say(view, 'ambient');
    }
  };

  const ambientGap = (): Ms => {
    const mean = AMBIENT_MEAN_MS[0] + (AMBIENT_MEAN_MS[1] - AMBIENT_MEAN_MS[0]) * T.chatty;
    return -mean * Math.log(1 - rng.next() * 0.999);
  };

  /** The tank or a leader calls pulls; others rarely comment on plain pulls. */
  const isCaller = (view: BotView): boolean => view.world.units[unitId]?.role === 'tank' || persona.archetype === 'leader';

  const worstDier = (view: BotView): { id: UnitId; n: number } | null => {
    let best: { id: UnitId; n: number } | null = null;
    for (const [id, n] of deathsOf) if (id !== unitId && view.world.units[id] && (!best || n > best.n)) best = { id, n };
    return best;
  };

  const onPullStart = (view: BotView, out: Command[]): void => {
    combatSince = view.now;
    bigChecked = false;
    if (!view.world.encounter) say(view, 'pull', {}, { boost: isCaller(view) ? 1.5 : 0.3 });
    // Companion: whoever is away comes back on their own as soon as a pull starts.
    if (!unhinged && (state === 'afk' || state === 'wandering')) backReactAt ??= view.now + rng.range(400, 1500);
    // Unhinged: the odd mid-pull AFK (doorbell during a pull).
    if (unhinged && state === 'normal' && view.world.units[unitId]?.alive && rng.chance(T.afk * AFK_MID_PULL)) startAfk(view, out, true);
  };

  const onPullEnd = (view: BotView): void => {
    combatSince = null;
    oocSince = view.now;
    if (state !== 'normal' || view.world.mplus.phase !== 'running') return;
    const afkP = T.afk * (unhinged ? AFK_PER_GAP.unhinged : AFK_PER_GAP.companion);
    const wanderP = T.greed * (unhinged ? WANDER_PER_GAP.unhinged : WANDER_PER_GAP.companion);
    if (rng.chance(afkP)) pending.push({ at: view.now + rng.range(1500, 5000), cmd: 'afk' });
    else if (rng.chance(wanderP)) pending.push({ at: view.now + rng.range(1000, 3000), cmd: 'wander' });
  };

  const onDeath = (view: BotView, id: UnitId, events: readonly SimEvent[]): void => {
    const u = view.world.units[id];
    if (!u) return;
    const de = events.find((e) => e.type === 'death' && e.targetID === id);
    const abilityId = de?.abilityGameID;
    const ability = abilityId ? (view.content.ability(abilityId)?.name ?? '') : '';
    const n = (deathsOf.get(id) ?? 0) + 1;
    deathsOf.set(id, n);
    const key = `${id}|${abilityId ?? ''}`;
    const nTo = (deathsTo.get(key) ?? 0) + (abilityId ? 1 : 0);
    if (abilityId) deathsTo.set(key, nTo);
    const repeat = Math.max(n, nTo) - 1;
    const cause = ability ? `${u.name} died to ${ability}${nTo > 1 ? ` (${ordinal(nTo)} time)` : ''}` : `${u.name} died${n > 1 ? ` (${ordinal(n)} time)` : ''}`;
    const vars = { victim: u.name, ability: ability || 'that', n: nTo || n, nth: ordinal(nTo || n) };
    if (id === unitId) {
      addTilt(view, TILT.ownDeath);
      say(view, 'self_died', vars, { delayMs: rng.range(800, 3000) });
      return;
    }
    // Low tolerance: mistakes by weaker players grate more.
    addTilt(view, (TILT.death + TILT.repeatDeath * repeat) * (1.4 - T.tolerance * 0.8), { id, cause });
    const sit: ChatSituation = u.controller === 'human' ? 'human_died' : 'other_died';
    say(view, sit, vars, { delayMs: rng.range(600, 2500), boost: repeat > 0 ? 1.5 : 1 });
  };

  const onLeft = (view: BotView, id: UnitId, events: readonly SimEvent[]): void => {
    const ev = events.find((e) => e.type === 'party_leave' && e.sourceID === id);
    const reason = ev && ev.type === 'party_leave' ? ev.reason : 'abandon';
    if (state === 'quit') return;
    if (reason === 'afk') say(view, 'afk_kick', { victim: name(view, id) }, { delayMs: rng.range(500, 2500), boost: 1.2 });
    else say(view, 'someone_left', { victim: name(view, id) }, { delayMs: rng.range(800, 3000) });
    if (!unhinged || pending.some((p) => p.cmd === 'abandon' || p.cmd === 'quit')) return;
    const w = view.world;
    const lost = w.units[id];
    const left = w.partyIds.filter((x) => !w.units[x]?.departed).length;
    const base = left <= 2 ? ABANDON_CHANCE.few : lost?.role === 'tank' || lost?.role === 'healer' ? ABANDON_CHANCE.core : ABANDON_CHANCE.dps;
    if (rng.chance(base * (1.2 - 0.5 * T.patience))) pending.push({ at: view.now + rng.range(4000, 15_000), cmd: 'abandon', cause: `${name(view, id)} left` });
  };

  const onEvent = (view: BotView, e: SimEvent): void => {
    const w = view.world;
    if (e.type === 'interrupt' && e.sourceID === unitId) {
      say(view, 'kick_landed', { target: name(view, e.targetID) });
    } else if (e.type === 'cast') {
      const src = w.units[e.sourceID];
      if (!src || src.faction !== 'enemy' || !(e.castMs && e.castMs > 0)) return;
      const ea = view.content.enemyAbility(e.abilityGameID);
      if (!ea || ea.interruptible === false || ea.kickPriority === undefined) return;
      addTilt(view, TILT.kickMissed);
      say(view, 'kick_missed', { target: src.name });
    } else if (e.type === 'begincast') {
      const src = w.units[e.sourceID];
      if (!src || src.faction !== 'enemy' || !e.interruptible) return;
      const ea = view.content.enemyAbility(e.abilityGameID);
      if (ea?.kickPriority !== undefined && (persona.archetype === 'leader' || persona.archetype === 'tryhard')) say(view, 'interrupt_call', { target: src.name });
    } else if (e.type === 'chat' && e.sourceID !== unitId && !e.lineId) {
      // A human said something: sometimes answer.
      say(view, 'reply', { human: name(view, e.sourceID) }, { delayMs: rng.range(800, 2500) });
    } else if (e.type === 'presence' && e.status && e.sourceID !== unitId) {
      // Waiting on someone who wandered off or went AFK grates (tryhards most of all).
      const n = (awaysOf.get(e.sourceID) ?? 0) + 1;
      awaysOf.set(e.sourceID, n);
      const what = e.status === 'afk' ? 'went AFK' : 'ran off to a node';
      addTilt(view, (TILT.away + TILT.repeatAway * (n - 1)) * (1.4 - T.tolerance * 0.8), { id: e.sourceID, cause: `${name(view, e.sourceID)} ${what}${n > 1 ? ` (${ordinal(n)} time)` : ''}` });
    } else if (e.type === 'cache_open' || (e.type === 'object_break' && persona.archetype === 'goblin')) {
      say(view, 'loot', {}, { boost: persona.archetype === 'goblin' ? 1.5 : 0.5 });
    }
  };

  // ---- decide ----------------------------------------------------------------------------------

  return {
    unitId,
    skill: inner.skill,
    persona,
    mode,
    get state(): PersonaState {
      return state === 'normal' && tilt >= TILTED_AT ? 'tilted' : state;
    },
    get tilt() {
      return tilt;
    },
    incidents,
    get linesSaid() {
      return voice.said;
    },
    decide(view: BotView, now: Ms): Command[] {
      if (state === 'quit') return [];
      const self = view.world.units[unitId];
      if (!self || self.departed) {
        state = 'quit';
        return [];
      }
      const out: Command[] = [];
      decayTilt(now);
      observe(view, out);
      const m = view.world.mplus;
      const running = m.phase === 'running' || m.phase === 'pre';

      // Scheduled actions.
      if (pending.length && running) {
        const due = pending.filter((p) => p.at <= now);
        if (due.length) pending = pending.filter((p) => p.at > now);
        for (const p of due) {
          if ((state as PersonaState) === 'quit') break;
          if (p.cmd === 'quit') leave(view, out, 'ragequit', p);
          else if (p.cmd === 'abandon') leave(view, out, 'abandon', p);
          else if (p.cmd === 'afkKick') {
            if (state === 'afk' && afkForever) leave(view, out, 'afk', p);
          } else if (state === 'normal' && self.alive && !m.inCombat && !othersAway(view)) {
            if (p.cmd === 'afk') startAfk(view, out, false);
            else {
              const target = wanderTarget(view, self);
              if (target) startWander(view, out, target);
            }
          }
        }
      }
      for (const l of voice.due(now)) out.push(sayCmd(l));
      if ((state as PersonaState) === 'quit') return out;

      if (state === 'afk') {
        if (!afkLongSaid && now - awayStartedAt > 50_000) {
          afkLongSaid = true;
          say(view, 'afk_long', {}, { force: rng.chance(0.4) });
        }
        if (!unhinged && m.inCombat) backReactAt ??= now + rng.range(400, 1500);
        if (now >= afkUntil || (backReactAt !== null && now >= backReactAt) || !running) endAway(view, out);
        else return out;
      }
      if (state === 'wandering' && wander) {
        if (!unhinged && m.inCombat) backReactAt ??= now + rng.range(300, 1000);
        const obj = view.world.worldObjects?.find((o) => o.id === wander!.id);
        const timedOut = now - awayStartedAt > WANDER_GIVE_UP_MS;
        if (!self.alive || timedOut || !running || (backReactAt !== null && now >= backReactAt) || !obj || (obj.broken && wander.gatherUntil === null)) endAway(view, out);
        else if (wander.gatherUntil !== null) {
          if (now >= wander.gatherUntil) endAway(view, out);
          else return out;
        } else if (dist(self.pos, wander.pos) <= 2.5) {
          wander.gatherUntil = now + rng.range(GATHER_MS[0], GATHER_MS[1]);
          out.push({ type: 'stop', actorId: unitId });
          return out;
        } else {
          if (!self.move && now - wander.lastMoveAt > 1000) {
            wander.lastMoveAt = now;
            out.push({ type: 'move', actorId: unitId, dest: wander.pos });
          }
          return out;
        }
      }
      // The tank waiting on an away member says so now and then.
      if (self.role === 'tank' && !m.inCombat && now - oocSince > 8000) {
        const away = view.world.partyIds.find((id) => id !== unitId && view.world.units[id]?.presence);
        if (away) say(view, 'waiting', { victim: name(view, away) }, { boost: 0.15 });
      }
      out.push(...inner.decide(view, now));
      return out;
    },
  };

  function othersAway(view: BotView): boolean {
    return view.world.partyIds.some((id) => id !== unitId && view.world.units[id]?.presence);
  }
}

/** Distance from point p to segment ab. */
function segDist(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}
